import { useCallback, useLayoutEffect, useRef } from 'react';
import noop from 'lodash/noop';
import { di } from 'react-magnetic-di';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import traceUFOInteraction from '@atlaskit/react-ufo/trace-interaction';
import { Ati } from '@atlassian/ari/ati';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import type { MenuItem } from '@atlassian/universal-create/types';
import { useInitializePersistentIssueCreateModalView } from '@jira/issue-create__issue-create-modal/src/controllers/use-initialize-persistent-issue-create-modal-view/index.tsx';
import {
	GENERIC_GLOBAL_CREATE_BUTTON,
	MOBILE_GLOBAL_CREATE_BUTTON,
} from '@jira/issue-create__use-trigger-issue-create-modal/src/constants.tsx';
import { useTriggerIssueCreateModal } from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import type { TriggerPointKeyType } from '@jira/issue-create__use-trigger-issue-create-modal/src/types.tsx';
import { isSupportedUserAgent as isMobileUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import OnCreateClickDI from '@jira/platform__legacy-create-issue-legacy/src/onCreateClick.tsx';
import { GLOBAL_CREATE_BUTTON } from '@jira/platform__legacy-create-issue-legacy/src/TriggerSource.types.tsx';
import { useIsInitialLoadLegacyIssueCreate } from '@jira/platform__legacy-create-issue-legacy/src/useIsInitialLoadLegacyIssueCreate.tsx';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	type GicOptInStatusResourceData,
	gicOptInStatusResources,
} from '@jira/router-resources__global-issue-create-switch/src/gicOptInStatusResources.tsx';
import { useIsClientReady } from '@jira/ssr__ssr-toolkit/src/useIsClientReady.tsx';
import { GlobalIssueCreateFromUniversalCreateExperience } from './GlobalIssueCreateFromUniversalCreateExperience.tsx';

const createIssue = defineMessage({
	defaultMessage: 'Work item',
	id: 'atlassian-navigation.universal-create.create-button-aria-label',
	description: 'Create a new work item button aria-label text',
});

type UseCreateIssueItemOptions = {
	/** Callback for legacy issue creation */
	onCreateClick?: typeof OnCreateClickDI;
	/** Experience tracker for analytics */
	experienceTracker?: typeof GlobalIssueCreateFromUniversalCreateExperience;
};

const useConditionalGicOptInResource = functionUnionWithCondition(
	() => fg('render_modern_global_issue_create_modal_experience'),
	() => useResource(gicOptInStatusResources),
	() => ({ data: null, update: noop }),
);

// TODO: extract onclick logi https://hello.jira.atlassian.cloud/browse/NAVX-2658
// copied from src/packages/navigation-apps/atlassian-navigation/src/ui/create/utils.tsx to avoid circular dependency
const shouldDisableGICFlagCreation: () => boolean = () => {
	di(window);
	const JSW_TMP_BACKLOG_BOARD_REGEXP =
		/jira\/software\/projects\/[a-zA-Z0-9]+\/boards\/\d+(\/backlog)?/;
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return JSW_TMP_BACKLOG_BOARD_REGEXP.test(window.location.pathname);
};

export function useCreateIssueItem(options?: UseCreateIssueItemOptions): MenuItem {
	const {
		onCreateClick = OnCreateClickDI,
		experienceTracker = GlobalIssueCreateFromUniversalCreateExperience,
	} = options ?? {};
	const { formatMessage } = useIntl();
	const isClientReady = useIsClientReady();
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	useInitializePersistentIssueCreateModalView();

	const [{ isInitialLoad }, actions] = useIsInitialLoadLegacyIssueCreate();
	let currentIssueCreateVisibility: GicOptInStatusResourceData['gicOptInStatus'] | null = null;
	const gicOptInStateOverridden = useRef(false);
	const isAnonymous = useIsAnonymous();

	const isMobileExperienceEnabled = isMobileUserAgent();

	// copied from src/packages/navigation-apps/atlassian-navigation/src/ui/create/main.tsx#useLayoutEffect hook
	// TODO: delete this code for clean up issue-create-stop-using-gicoptinstatusresources
	const { data, update } = useConditionalGicOptInResource();

	useLayoutEffect(() => {
		if (!fg('render_modern_global_issue_create_modal_experience')) {
			return;
		}
		if (!isClientReady) {
			// Return nothing during SSR
			return;
		}

		if (isAnonymous && !gicOptInStateOverridden.current) {
			update(() => ({
				gicOptInStatus: {
					isEnabled: true,
				},
			}));
			gicOptInStateOverridden.current = true;
		}
	}, [isAnonymous, update, isClientReady]);

	currentIssueCreateVisibility = data && data?.gicOptInStatus;

	const onClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		(e) => {
			if (currentIssueCreateVisibility?.isEnabled) {
				traceUFOInteraction('global-create.work-item-click', e);
				experienceTracker.abort({ force: true });
				experienceTracker.start();
				experienceTracker.addMetadata({
					inputDelay: Math.round(performance.now() - e.timeStamp),
				});

				const nextState: {
					disableCreationSuccessFlag: boolean;
					triggerPointKey: TriggerPointKeyType;
					displayMinimizableButtons?: boolean;
					displayCreateAnotherIssueToggle?: boolean;
				} = {
					disableCreationSuccessFlag: fg('show_top_nav_gic_success_flag')
						? false
						: shouldDisableGICFlagCreation(),
					triggerPointKey: GENERIC_GLOBAL_CREATE_BUTTON,
					...(isMobileExperienceEnabled
						? {
								displayMinimizableButtons: false,
								displayCreateAnotherIssueToggle: false,
								triggerPointKey: MOBILE_GLOBAL_CREATE_BUTTON,
							}
						: {}),
				};

				openIssueCreateModal(nextState);
			} else {
				onCreateClick({ isInitialLoad, triggerSource: GLOBAL_CREATE_BUTTON });
				if (isInitialLoad) {
					actions.clearInitialLoaded();
				}
			}
		},
		[
			actions,
			currentIssueCreateVisibility,
			experienceTracker,
			isInitialLoad,
			isMobileExperienceEnabled,
			onCreateClick,
			openIssueCreateModal,
		],
	);

	return {
		ati: Ati.parse('ati:cloud:jira:issue:create'),
		elemBefore: WorkItemIcon,
		children: formatMessage(createIssue),
		onClick: isClientReady ? onClick : undefined,
	};
}
